/**
 * Bounded retry for plain `<img>` loads — ENTR's counterpart of the
 * kiosk-shared imageLoader retry policy (Vue app, so the Solid KioskImage
 * can't be reused; the policy is mirrored instead).
 *
 * The screens previously dropped an image permanently on its FIRST error:
 * one venue-Wi-Fi blip and a portrait cell stayed a skeleton for the whole
 * session (and the visual harness silently captured that skeleton). Now an
 * error schedules a cache-busted reload with backoff; only after
 * `maxAttempts` does the slot settle into the errored state — which the
 * screens expose as `data-image-error` so the visual harness fails LOUDLY
 * instead of shipping a wrong screenshot.
 */

import { onScopeDispose, shallowRef } from 'vue';

export interface ImageRetryOptions {
  /** Total load attempts per key (first try included). Default 4. */
  maxAttempts?: number;
  /** Delay before each retry, indexed by retry number. Default [400, 1200, 3000]. */
  backoffMs?: readonly number[];
  onExhausted?: (key: string, attempts: number) => void;
}

export interface ImageRetry {
  /** Reactive src: cache-busted after each failed attempt so the browser re-requests. */
  src(key: string, baseSrc: string): string;
  onLoad(key: string): void;
  onError(key: string): void;
  isLoaded(key: string): boolean;
  /** True only after ALL attempts failed. */
  isErrored(key: string): boolean;
}

export function createImageRetry(options: ImageRetryOptions = {}): ImageRetry {
  const maxAttempts = options.maxAttempts ?? 4;
  const backoffMs = options.backoffMs ?? [400, 1200, 3000];

  const attempts = shallowRef<ReadonlyMap<string, number>>(new Map());
  const loaded = shallowRef<ReadonlySet<string>>(new Set());
  const errored = shallowRef<ReadonlySet<string>>(new Set());
  const timers = new Set<ReturnType<typeof setTimeout>>();

  onScopeDispose(() => {
    for (const timer of timers) clearTimeout(timer);
    timers.clear();
  });

  const bumpAttempt = (key: string): void => {
    const next = new Map(attempts.value);
    next.set(key, (next.get(key) ?? 0) + 1);
    attempts.value = next;
  };

  return {
    src(key, baseSrc) {
      const attempt = attempts.value.get(key) ?? 0;
      if (!baseSrc || attempt === 0) return baseSrc;
      const joiner = baseSrc.includes('?') ? '&' : '?';
      return `${baseSrc}${joiner}retry=${attempt}`;
    },
    onLoad(key) {
      if (!loaded.value.has(key)) {
        loaded.value = new Set(loaded.value).add(key);
      }
      if (errored.value.has(key)) {
        const next = new Set(errored.value);
        next.delete(key);
        errored.value = next;
      }
    },
    onError(key) {
      const failedAttempts = (attempts.value.get(key) ?? 0) + 1;
      if (failedAttempts >= maxAttempts) {
        if (!errored.value.has(key)) {
          errored.value = new Set(errored.value).add(key);
          options.onExhausted?.(key, failedAttempts);
        }
        if (loaded.value.has(key)) {
          const next = new Set(loaded.value);
          next.delete(key);
          loaded.value = next;
        }
        return;
      }
      const delay =
        backoffMs[Math.min(failedAttempts - 1, backoffMs.length - 1)];
      const timer = setTimeout(() => {
        timers.delete(timer);
        // Bumping the attempt changes src(), which re-triggers the load.
        bumpAttempt(key);
      }, delay);
      timers.add(timer);
    },
    isLoaded: (key) => loaded.value.has(key),
    isErrored: (key) => errored.value.has(key),
  };
}

export interface KeyedImageRetry<T> {
  src(item: T): string;
  onLoad(item: T): void;
  onError(item: T): void;
  isLoaded(item: T): boolean;
  isErrored(item: T): boolean;
}

export function keyedImageRetry<T>(
  keyOf: (item: T) => string,
  srcOf: (item: T) => string,
  options: ImageRetryOptions = {}
): KeyedImageRetry<T> {
  const retry = createImageRetry(options);
  return {
    src: (item) => retry.src(keyOf(item), srcOf(item)),
    onLoad: (item) => retry.onLoad(keyOf(item)),
    onError: (item) => retry.onError(keyOf(item)),
    isLoaded: (item) => retry.isLoaded(keyOf(item)),
    isErrored: (item) => retry.isErrored(keyOf(item)),
  };
}
